import React, { useCallback, useEffect, useRef, useState } from 'react';

import SectionHead from './SectionHead';
import { StarIcon } from '../ui/icons';
import { reviews as defaultReviews } from '../data/site';

/**
 * A native horizontal scroller with snap points, not a carousel library.
 * It keeps the keyboard, the trackpad and the screen reader working, and
 * ships no JavaScript for the scrolling itself: the only script here is
 * the drag-to-pan affordance for mouse users, who have no other way to
 * move a horizontal scroller, and the dot indicator.
 */
export default function Reviews({ items = defaultReviews, eyebrow = 'Clients', title }) {
    const railRef = useRef(null);
    const [active, setActive] = useState(0);
    const drag = useRef(null);

    const onScroll = useCallback(() => {
        const rail = railRef.current;
        if (!rail) return;

        const middle = rail.scrollLeft + rail.clientWidth / 2;
        let nearest = 0;
        let best = Infinity;

        for (let i = 0; i < rail.children.length; i += 1) {
            const card = rail.children[i];
            const centre = card.offsetLeft + card.offsetWidth / 2;
            const gap = Math.abs(centre - middle);

            if (gap < best) {
                best = gap;
                nearest = i;
            }
        }

        setActive(nearest);
    }, []);

    /* Pointer-drag panning. `setPointerCapture` is what keeps the gesture
       alive when the cursor leaves the rail mid-drag; without it the rail
       stops tracking and the page is left in a half-dragged state. */
    const onPointerDown = event => {
        if (event.pointerType !== 'mouse') return;

        drag.current = { x: event.clientX, left: railRef.current.scrollLeft, moved: false };
        railRef.current.setPointerCapture(event.pointerId);
    };

    const onPointerMove = event => {
        if (!drag.current) return;

        const dx = event.clientX - drag.current.x;
        if (Math.abs(dx) > 3) drag.current.moved = true;

        railRef.current.scrollLeft = drag.current.left - dx;
    };

    const onPointerUp = event => {
        if (!drag.current) return;

        railRef.current.releasePointerCapture(event.pointerId);
        drag.current = null;
    };

    useEffect(() => {
        onScroll();
    }, [onScroll]);

    const goTo = index => {
        const card = railRef.current?.children[index];
        card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };

    return (
        <section className="section section--raised reviews">
            <div className="shell">
                <SectionHead
                    eyebrow={eyebrow}
                    title={title || 'What our clients say.'}
                    center
                />
            </div>

            <div
                className="reviews__rail"
                ref={railRef}
                onScroll={onScroll}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                tabIndex={0}
                role="region"
                aria-label="Client reviews"
            >
                {items.map(review => (
                    <article className="reviews__card" key={review.name}>
                        <div className="reviews__stars" aria-label="Five out of five stars">
                            {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} />)}
                        </div>

                        <p className="reviews__quote">“{review.text}”</p>

                        <p className="reviews__name">
                            {review.name}
                            {review.role && <small>{review.role}</small>}
                        </p>
                    </article>
                ))}
            </div>

            <div className="reviews__dots">
                {items.map((review, i) => (
                    <button
                        key={review.name}
                        type="button"
                        className="reviews__dot"
                        aria-current={i === active ? 'true' : 'false'}
                        aria-label={`Go to review ${i + 1}`}
                        onClick={() => goTo(i)}
                    />
                ))}
            </div>
        </section>
    );
}
